import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Save, Eye, Download, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProgrammesTab({ competitions }) {
  const navigate = useNavigate();
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [programme, setProgramme] = useState({ rounds: [], isVisible: false });
  const [availableGymnasts, setAvailableGymnasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasProgramme, setHasProgramme] = useState(false);
  // Per-group search terms: { "roundIndex-groupIndex": "searchTerm" }
  const [groupSearchTerms, setGroupSearchTerms] = useState({});

  useEffect(() => {
    if (selectedCompetition) {
      loadProgramme();
      loadAvailableGymnasts();
    }
  }, [selectedCompetition]);

  const loadProgramme = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/programmes/${selectedCompetition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Migrate old format to new format
      const programmeData = response.data;
      
      // Check if programme exists (has rounds with groups)
      const programmeExists = programmeData.rounds && programmeData.rounds.length > 0;
      setHasProgramme(programmeExists);
      
      if (programmeData.rounds) {
        programmeData.rounds.forEach(round => {
          round.groups = round.groups || [];
          round.groups.forEach(group => {
            // Convert old gymnastIds format to new gymnasts format
            if (group.gymnastIds && !group.gymnasts) {
              group.gymnasts = group.gymnastIds.map(id => ({
                gymnastId: id,
                number: '',
                letter: ''
              }));
              delete group.gymnastIds;
            } else if (!group.gymnasts) {
              group.gymnasts = [];
            }
          });
        });
      }
      
      setProgramme(programmeData);
    } catch (error) {
      console.error('Failed to load programme:', error);
      toast.error('Failed to load programme');
      setHasProgramme(false);
    }
  };

  const loadAvailableGymnasts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/programmes/${selectedCompetition}/available-gymnasts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableGymnasts(response.data);
    } catch (error) {
      console.error('Failed to load gymnasts:', error);
      toast.error('Failed to load gymnasts');
    }
  };

  // Remove global dual panel toggle - now per-round
  
  const handleAddRound = () => {
    const newRound = {
      roundNumber: programme.rounds.length + 1,
      roundName: `Round ${programme.rounds.length + 1}`,
      isDualPanel: false, // Per-round dual panel setting
      groups: []
    };
    setProgramme({ ...programme, rounds: [...programme.rounds, newRound] });
  };

  const handleDeleteRound = (roundIndex) => {
    const updatedRounds = programme.rounds.filter((_, i) => i !== roundIndex);
    // Don't auto-renumber - keep manual numbers
    setProgramme({ ...programme, rounds: updatedRounds });
  };

  // Update round number manually
  const handleRoundNumberChange = (roundIndex, value) => {
    const rounds = [...programme.rounds];
    rounds[roundIndex].roundNumber = parseInt(value) || 1;
    rounds[roundIndex].roundName = `Round ${rounds[roundIndex].roundNumber}`;
    setProgramme({ ...programme, rounds });
  };

  // Toggle dual panel for specific round
  const handleToggleRoundDualPanel = (roundIndex) => {
    const rounds = [...programme.rounds];
    rounds[roundIndex].isDualPanel = !rounds[roundIndex].isDualPanel;
    setProgramme({ ...programme, rounds });
  };

  const handleAddGroup = (roundIndex) => {
    const rounds = [...programme.rounds];
    // Default to next letter in sequence for this round only
    const existingLetters = rounds[roundIndex].groups.map(g => {
      const match = g.groupName?.match(/Group\s*([A-Z])/i);
      return match ? match[1].toUpperCase() : null;
    }).filter(Boolean);
    
    let nextLetter = 'A';
    if (existingLetters.length > 0) {
      const maxChar = existingLetters.sort().pop();
      nextLetter = String.fromCharCode(maxChar.charCodeAt(0) + 1);
    }
    
    rounds[roundIndex].groups.push({
      groupName: `Group ${nextLetter}`,
      gymnasts: []
    });
    setProgramme({ ...programme, rounds });
  };

  // Update group name/letter manually
  const handleGroupNameChange = (roundIndex, groupIndex, value) => {
    const rounds = [...programme.rounds];
    rounds[roundIndex].groups[groupIndex].groupName = value;
    setProgramme({ ...programme, rounds });
  };

  const handleDeleteGroup = (roundIndex, groupIndex) => {
    const rounds = [...programme.rounds];
    rounds[roundIndex].groups = rounds[roundIndex].groups.filter((_, i) => i !== groupIndex);
    // Don't auto-rename - keep manual names
    setProgramme({ ...programme, rounds });
  };

  const handleToggleGymnast = (roundIndex, groupIndex, gymnastId) => {
    const rounds = [...programme.rounds];
    const group = rounds[roundIndex].groups[groupIndex];
    
    const existingIndex = group.gymnasts.findIndex(g => g.gymnastId === gymnastId);
    if (existingIndex >= 0) {
      group.gymnasts.splice(existingIndex, 1);
    } else {
      // Auto-inherit the group's current panel (from first gymnast that has one set)
      const currentPanel = (group.gymnasts || []).find(g => g.letter)?.letter || '';
      group.gymnasts.push({
        gymnastId: gymnastId,
        number: '',
        letter: currentPanel
      });
    }
    
    setProgramme({ ...programme, rounds });
  };

  const handleUpdateGymnastNumber = (roundIndex, groupIndex, gymnastId, field, value) => {
    const rounds = [...programme.rounds];
    const group = rounds[roundIndex].groups[groupIndex];
    const gymnastIndex = group.gymnasts.findIndex(g => g.gymnastId === gymnastId);
    
    if (gymnastIndex >= 0) {
      group.gymnasts[gymnastIndex][field] = value;
      setProgramme({ ...programme, rounds });
    }
  };

  const getAssignedGymnastIds = () => {
    const ids = new Set();
    programme.rounds.forEach(round => {
      round.groups.forEach(group => {
        // Handle both old (gymnastIds) and new (gymnasts) format
        if (group.gymnasts && Array.isArray(group.gymnasts)) {
          group.gymnasts.forEach(g => ids.add(g.gymnastId));
        } else if (group.gymnastIds && Array.isArray(group.gymnastIds)) {
          group.gymnastIds.forEach(id => ids.add(id));
        }
      });
    });
    return ids;
  };

  const getAvailableGymnastsForGroup = (roundIndex, groupIndex) => {
    const assignedIds = getAssignedGymnastIds();
    const currentGroup = programme.rounds[roundIndex].groups[groupIndex];
    
    // Handle both old and new format
    let currentGroupGymnastIds = [];
    if (currentGroup.gymnasts && Array.isArray(currentGroup.gymnasts)) {
      currentGroupGymnastIds = currentGroup.gymnasts.map(g => g.gymnastId);
    } else if (currentGroup.gymnastIds && Array.isArray(currentGroup.gymnastIds)) {
      currentGroupGymnastIds = currentGroup.gymnastIds;
    }
    
    return availableGymnasts.filter(gymnast => {
      return currentGroupGymnastIds.includes(gymnast.id) || !assignedIds.has(gymnast.id);
    });
  };

  const isGymnastInGroup = (roundIndex, groupIndex, gymnastId) => {
    const group = programme.rounds[roundIndex].groups[groupIndex];
    
    // Handle both old and new format
    if (group.gymnasts && Array.isArray(group.gymnasts)) {
      return group.gymnasts.some(g => g.gymnastId === gymnastId);
    } else if (group.gymnastIds && Array.isArray(group.gymnastIds)) {
      return group.gymnastIds.includes(gymnastId);
    }
    return false;
  };

  const getGymnastData = (roundIndex, groupIndex, gymnastId) => {
    const group = programme.rounds[roundIndex].groups[groupIndex];
    
    // Handle both old and new format
    if (group.gymnasts && Array.isArray(group.gymnasts)) {
      return group.gymnasts.find(g => g.gymnastId === gymnastId);
    }
    // For old format, return empty object
    return { gymnastId, number: '', letter: '' };
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/superadmin/programmes/${selectedCompetition}`,
        {
          rounds: programme.rounds,
          // Global isDualPanel is kept for backward compatibility but per-round takes precedence
          isDualPanel: false,
          isVisible: programme.isVisible
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Programme saved successfully');
      // Programme now exists after saving
      setHasProgramme(true);
    } catch (error) {
      console.error('Failed to save programme:', error);
      toast.error('Failed to save programme');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superadmin/programmes/${selectedCompetition}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const competition = competitions.find(c => c.id === selectedCompetition);
      const filename = competition ? `${competition.title} - Programme.pdf` : 'Programme.pdf';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Programme exported successfully');
    } catch (error) {
      console.error('Failed to export programme:', error);
      toast.error('Failed to export programme');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>Competition Programmes</h2>
        <p className="text-gray-600 text-sm mt-1">Create and manage rounds and groups for competitions</p>
      </div>

      {/* Competition Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Competition</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a competition..." />
            </SelectTrigger>
            <SelectContent>
              {competitions.map(comp => (
                <SelectItem key={comp.id} value={comp.id}>
                  {comp.title} - {comp.displayDate}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedCompetition && (
        <>
          {/* Settings Card */}
          <Card className="mb-4">
            <CardContent className="pt-6 space-y-4">
              {/* Visibility Toggle */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isVisible"
                  checked={programme.isVisible}
                  onCheckedChange={(checked) => setProgramme({...programme, isVisible: checked})}
                />
                <Label htmlFor="isVisible" className="cursor-pointer">
                  <span className="font-semibold">Visible to Clubs</span>
                  <span className="text-sm text-gray-600 ml-2">
                    {programme.isVisible ? '(Programme is visible to clubs)' : '(Programme is hidden - clubs see "Programme not yet published")'}
                  </span>
                </Label>
              </div>
              <p className="text-xs text-gray-500">
                Note: Dual Panel can be set individually for each round below.
              </p>
            </CardContent>
          </Card>

          <div className="mb-4 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Available Gymnasts: {availableGymnasts.length - getAssignedGymnastIds().size} of {availableGymnasts.length}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleAddRound}
                className="bg-[#000000] hover:bg-[#333333]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Round
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Programme'}
              </Button>
              {hasProgramme && (
                <>
                  <Button
                    onClick={() => navigate(`/superadmin/view-programme/${selectedCompetition}`)}
                    variant="outline"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Programme
                  </Button>
                  <Button
                    onClick={handleExportPDF}
                    variant="outline"
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Rounds */}
          <div className="space-y-6">
            {programme.rounds.map((round, roundIndex) => (
              <Card key={roundIndex}>
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      {/* Editable Round Number */}
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium whitespace-nowrap">Round</Label>
                        <Input
                          type="number"
                          min="1"
                          value={round.roundNumber || ''}
                          onChange={(e) => handleRoundNumberChange(roundIndex, e.target.value)}
                          className="w-20 h-8"
                          placeholder="#"
                        />
                      </div>
                      
                      {/* Per-Round Dual Panel Toggle */}
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`dualPanel-${roundIndex}`}
                          checked={round.isDualPanel || false}
                          onCheckedChange={() => handleToggleRoundDualPanel(roundIndex)}
                        />
                        <Label htmlFor={`dualPanel-${roundIndex}`} className="text-sm cursor-pointer">
                          Dual Panel
                        </Label>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAddGroup(roundIndex)}
                        size="sm"
                        variant="outline"
                        className="text-[#000000] border-[#000000]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Group
                      </Button>
                      <Button
                        onClick={() => handleDeleteRound(roundIndex)}
                        size="sm"
                        variant="outline"
                        className="text-gray-900 border-gray-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {round.groups.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No groups yet. Click "Add Group".</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {round.groups.map((group, groupIndex) => (
                        <Card key={groupIndex} className="border-2">
                          <CardHeader className="pb-3">
                            <div className="flex justify-between items-center gap-2">
                              {/* Editable Group Name */}
                              <Input
                                type="text"
                                value={group.groupName || ''}
                                onChange={(e) => handleGroupNameChange(roundIndex, groupIndex, e.target.value)}
                                className="w-40 h-8 font-semibold"
                                placeholder="Group A"
                              />
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  {(group.gymnasts || group.gymnastIds || []).length} gymnasts
                                </span>
                                <Button
                                  onClick={() => handleDeleteGroup(roundIndex, groupIndex)}
                                  size="sm"
                                  variant="ghost"
                                  className="text-gray-900"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {/* Search Bar - Independent per group */}
                            <div className="mb-4">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                  type="text"
                                  placeholder="Search by gymnast name or club..."
                                  value={groupSearchTerms[`${roundIndex}-${groupIndex}`] || ''}
                                  onChange={(e) => setGroupSearchTerms(prev => ({
                                    ...prev,
                                    [`${roundIndex}-${groupIndex}`]: e.target.value
                                  }))}
                                  className="pl-10"
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                              {getAvailableGymnastsForGroup(roundIndex, groupIndex)
                                .filter(gymnast => {
                                  const searchTerm = groupSearchTerms[`${roundIndex}-${groupIndex}`] || '';
                                  if (!searchTerm) return true;
                                  const search = searchTerm.toLowerCase();
                                  const fullName = `${gymnast.displayFirstName || gymnast.preferredFirstName || gymnast.firstName} ${gymnast.displayLastName || gymnast.preferredLastName || gymnast.lastName}`.toLowerCase();
                                  const clubName = (gymnast.clubName || '').toLowerCase();
                                  const category = (gymnast.category || '').toLowerCase();
                                  return fullName.includes(search) || clubName.includes(search) || category.includes(search);
                                })
                                .map(gymnast => {
                                const isSelected = isGymnastInGroup(roundIndex, groupIndex, gymnast.id);
                                const gymnastData = getGymnastData(roundIndex, groupIndex, gymnast.id);
                                
                                return (
                                  <div key={gymnast.id} className="border rounded p-3 bg-gray-50">
                                    <div className="flex items-center space-x-2 mb-2">
                                      <Checkbox
                                        id={`${roundIndex}-${groupIndex}-${gymnast.id}`}
                                        checked={isSelected}
                                        onCheckedChange={() => handleToggleGymnast(roundIndex, groupIndex, gymnast.id)}
                                      />
                                      <Label
                                        htmlFor={`${roundIndex}-${groupIndex}-${gymnast.id}`}
                                        className="text-sm cursor-pointer flex-1 font-medium"
                                      >
                                        {gymnast.displayFirstName || gymnast.preferredFirstName || gymnast.firstName} {gymnast.displayLastName || gymnast.preferredLastName || gymnast.lastName}
                                        <span className="text-xs text-gray-500 ml-2">({gymnast.clubName})</span>
                                        {gymnast.category && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded ml-2">{gymnast.category}</span>}
                                      </Label>
                                    </div>
                                    
                                    {isSelected && (
                                      <div className="ml-6 mt-2 flex gap-2">
                                        <div className="flex-1">
                                          <Label htmlFor={`number-${roundIndex}-${groupIndex}-${gymnast.id}`} className="text-xs">
                                            Number *
                                          </Label>
                                          <Input
                                            id={`number-${roundIndex}-${groupIndex}-${gymnast.id}`}
                                            value={gymnastData?.number || ''}
                                            onChange={(e) => handleUpdateGymnastNumber(roundIndex, groupIndex, gymnast.id, 'number', e.target.value)}
                                            placeholder="e.g. 23"
                                            className="h-8 text-sm"
                                          />
                                        </div>
                                        {round.isDualPanel && (
                                          <div className="w-20">
                                            <Label htmlFor={`letter-${roundIndex}-${groupIndex}-${gymnast.id}`} className="text-xs">
                                              Panel *
                                            </Label>
                                            <Select
                                              value={gymnastData?.letter || ''}
                                              onValueChange={(value) => handleUpdateGymnastNumber(roundIndex, groupIndex, gymnast.id, 'letter', value)}
                                            >
                                              <SelectTrigger className="h-8 text-sm">
                                                <SelectValue placeholder="?" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="A">A</SelectItem>
                                                <SelectItem value="B">B</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {getAvailableGymnastsForGroup(roundIndex, groupIndex).length === 0 && (
                                <p className="text-xs text-gray-500 text-center py-2">All gymnasts assigned</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {programme.rounds.length === 0 && (
            <Card>
              <CardContent className="py-12">
                <p className="text-center text-gray-500">No rounds created yet. Click "Add Round" to start.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!selectedCompetition && (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-gray-500">Select a competition to manage its programme</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
